import { SIZE_LOCATIONS_PAGE } from 'const/paging';
import { useAsyncCallback } from 'hooks/useAsyncCallback';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getLocations, getFloorPlan } from 'services/locations';
import { RoomInfo } from 'types/room';
import { isVerticalLocationLevel } from 'utils/renderTitleLocation';

export const useInjection = () => {
  const navigate = useNavigate();
  const { clientId, locationId, clientName } = useParams();
  const [roomInfo, setRoomInfo] = useState<RoomInfo>([]);
  const [floorPlanUrl, setfloorplanurl] = useState<string>();
  const [isLoading, setLoading] = useState<boolean>(true);

  const handleGetInitLocations = useCallback(async () => {
    const params = {
      size: SIZE_LOCATIONS_PAGE,
      page: 1,
      parentId: locationId ? locationId : undefined,
      clientId: clientId ? clientId : undefined
    };
    const data = (await getLocations(params)) as {
      room: RoomInfo;
    };
    setRoomInfo(data.room);
  }, [clientId, locationId]);

  const { asyncCallback: asyncGetInitLocations } = useAsyncCallback(handleGetInitLocations, []);

  const floorplan = useCallback(async () => {
    setLoading(true);
    const payload = {
      id: Number(locationId)
    };
    try {
      const imgurl = await getFloorPlan(payload);
      setfloorplanurl(imgurl.data.floorPlan[0].url);
      setLoading(false);
    } catch (e) {
      setLoading(false);
    } finally {
      setLoading(false);
    }
  }, [locationId]);

  const { asyncCallback: asyncfloorplan } = useAsyncCallback(floorplan, []);

  const paths: RoomInfo = useMemo(() => {
    return [
      { name: 'PDF Mapping', level: 999, onClick: () => navigate('/pdfmapping') },
      {
        name: clientName || '',
        id: Number(clientId),
        isClient: true,
        level: 998,
        onClick: () => navigate(`/pdfmapping/uploadplan/${clientName}/${clientId}/location`)
      },
      ...roomInfo
        .sort((a, b) => a.level - b.level)
        .map((item) => {
          const href = isVerticalLocationLevel(item.level)
            ? `/pdfmapping/uploadplan/${clientName}/${clientId}/location/${item.id}/view`
            : `/pdfmapping/uploadplan/${clientName}/${clientId}/location/${item.id}`;
          return {
            ...item,
            onClick: () => navigate(href)
          };
        })
    ];
  }, [clientId, clientName, navigate, roomInfo]);

  useEffect(() => {
    asyncGetInitLocations();
  }, [asyncGetInitLocations]);

  useEffect(() => {
    asyncfloorplan();
  }, [asyncfloorplan]);

  const handleBack = useCallback(() => {
    if (!locationId) {
      return navigate('/pdfmapping');
    }

    const pathSegments = location.pathname.split('/');
    if (pathSegments.length > 1) {
      pathSegments.pop();
      navigate(pathSegments.join('/'));
    }
  }, [location.pathname, locationId, navigate]);

  return {
    paths,
    handleBack,
    floorPlanUrl,
    isLoading
  };
};
